import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { updateFormDataField } from '../formSlice'
import { createFormDataFieldSelector } from '../helpers/createFormDataFieldSelector'
import { TUseGetFormDataFieldResult } from '../models'
import { FormValidationBuilder } from '../utils/validation'

/*
 * This hook returns form field by form key and field name and handler to change this field
 * */
export const useGetFormDataField = <T>(
  formKey: string,
  field: string
): TUseGetFormDataFieldResult<T> => {
  const dispatch = useAppDispatch()
  const formValidation = FormValidationBuilder.build(formKey)
  const selectModifiedField = useMemo(
    () => createFormDataFieldSelector<T>(formKey, field),
    [formKey, field]
  )
  const { value, isModified } = useSelector(
    selectModifiedField,
    (prev, next) => prev.value === next.value && prev.isModified === next.isModified
  )

  const handleChange = useCallback(
    async (value: T, doRevalidate?: boolean) => {
      dispatch(updateFormDataField({ formKey, field, value }))

      if (doRevalidate) {
        await formValidation.call(field, value)
      }
    },
    [dispatch, field, formKey, formValidation]
  )

  return [value, handleChange, isModified]
}
